'use client';

import { useEffect, useRef, useCallback } from 'react';
import { getSlideName } from './trackingStore';

// Generate or retrieve a stable visitor ID for this browser session
function getVisitorId(): string {
  if (typeof window === 'undefined') return '';
  let id = sessionStorage.getItem('x39_visitor_id');
  if (!id) {
    id = `v-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem('x39_visitor_id', id);
  }
  return id;
}

interface TrackingOptions {
  currentSlide: number;
  leadId?: string;
  leadData?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  path?: 'A' | 'B' | null;
  orderStarted?: boolean;
  orderSubmitted?: boolean;
}

export function useTracking(options: TrackingOptions) {
  const { currentSlide, leadId, leadData, path, orderStarted, orderSubmitted } = options;
  const visitorId = useRef<string>('');
  const slideEnteredAt = useRef<number>(Date.now());
  const totalTimeRef = useRef<number>(0);
  const lastFlushSlide = useRef<number>(0);

  // Initialize visitor ID on mount
  useEffect(() => {
    visitorId.current = getVisitorId();
  }, []);

  // Flush current slide time and send update
  const flush = useCallback(
    async (overrideSlide?: number, extraData?: Record<string, unknown>) => {
      if (!visitorId.current) return;
      const slide = overrideSlide ?? currentSlide;
      const now = Date.now();
      const elapsed = Math.round((now - slideEnteredAt.current) / 1000);
      totalTimeRef.current += elapsed;

      try {
        await fetch('/api/tracking', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitorId: visitorId.current,
            leadId,
            leadData,
            path,
            orderStarted: orderStarted ?? false,
            orderSubmitted: orderSubmitted ?? false,
            pageVisit: {
              slide,
              slideName: getSlideName(slide),
              enteredAt: new Date(slideEnteredAt.current).toISOString(),
              timeSpentSeconds: elapsed,
            },
            totalTimeSeconds: totalTimeRef.current,
            maxSlideReached: slide,
            ...extraData,
          }),
        });
      } catch {
        // Silently fail — tracking should never break the app
      }
    },
    [currentSlide, leadId, leadData, path, orderStarted, orderSubmitted]
  );

  // When slide changes: flush previous slide, reset timer
  useEffect(() => {
    if (!visitorId.current) {
      visitorId.current = getVisitorId();
    }
    if (lastFlushSlide.current !== 0 && lastFlushSlide.current !== currentSlide) {
      flush(lastFlushSlide.current);
    }
    slideEnteredAt.current = Date.now();
    lastFlushSlide.current = currentSlide;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => {
      if (!visitorId.current) return;
      const elapsed = Math.round((Date.now() - slideEnteredAt.current) / 1000);
      totalTimeRef.current += elapsed;
      // Use sendBeacon for reliability on unload
      navigator.sendBeacon(
        '/api/tracking',
        JSON.stringify({
          visitorId: visitorId.current,
          leadId,
          leadData,
          path,
          orderStarted: orderStarted ?? false,
          orderSubmitted: orderSubmitted ?? false,
          pageVisit: {
            slide: currentSlide,
            slideName: getSlideName(currentSlide),
            enteredAt: new Date(slideEnteredAt.current).toISOString(),
            timeSpentSeconds: elapsed,
          },
          totalTimeSeconds: totalTimeRef.current,
          maxSlideReached: currentSlide,
        })
      );
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, leadId, path, orderStarted, orderSubmitted]);

  // Flush when orderSubmitted or orderStarted changes
  useEffect(() => {
    if (visitorId.current) {
      flush(currentSlide);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSubmitted, orderStarted]);

  // Track a click event
  const trackClick = useCallback(
    async (action: string, label: string) => {
      if (!visitorId.current) return;
      try {
        await fetch('/api/tracking', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            visitorId: visitorId.current,
            leadId,
            leadData,
            path,
            orderStarted: orderStarted ?? false,
            orderSubmitted: orderSubmitted ?? false,
            clickEvent: {
              action,
              label,
              slide: currentSlide,
              timestamp: new Date().toISOString(),
            },
            totalTimeSeconds: totalTimeRef.current,
            maxSlideReached: currentSlide,
          }),
        });
      } catch {
        // Silently fail
      }
    },
    [currentSlide, leadId, leadData, path, orderStarted, orderSubmitted]
  );

  return { trackClick, visitorId: visitorId.current };
}
